<template>
  <section class="container-fluid mt-5 py-5">
    <div class="container mt-5">
      <div class="row justify-content-center">
        <div class="col-md-8">
          <div class="card bg-dark text-light">
            <div class="card-body p-4">
              <h2 class="mb-4">{{ isEditing ? 'Editar' : 'Nuevo' }} Banco<span class="text-primary">.</span></h2>

              <form @submit.prevent="handleSubmit" class="needs-validation">
                <!-- Logo/Imagen -->
                <div class="mb-4">
                  <label class="form-label">Logo del Banco</label>
                  <div class="d-flex align-items-center gap-3">
                    <img 
                      :src="form.image || defaultImage" 
                      alt="Logo preview"
                      class="img-thumbnail"
                      style="width: 100px; height: 100px; object-fit: cover;"
                    />
                    <div class="flex-grow-1">
                      <input 
                        type="file"
                        class="form-control"
                        @change="handleImageUpload"
                        accept="image/*"
                      />
                      <div v-if="uploadProgress > 0 && uploadProgress < 100" class="progress mt-2">
                        <div 
                          class="progress-bar" 
                          role="progressbar" 
                          :style="{ width: uploadProgress + '%' }"
                          :aria-valuenow="uploadProgress" 
                          aria-valuemin="0" 
                          aria-valuemax="100"
                        >
                          {{ uploadProgress }}%
                        </div>
                      </div>
                      <small class="text-muted">Subí el logo en formato PNG del banco (máximo 2MB)</small>
                    </div>
                  </div>
                </div>

                <!-- Nombre del Banco -->
                <div class="mb-4">
                  <label class="form-label required">Nombre del Banco</label>
                  <input 
                    type="text"
                    class="form-control"
                    v-model="form.brand_name"
                    required
                    :class="{ 'is-invalid': errors.brand_name }"
                  />
                  <div class="invalid-feedback" v-if="errors.brand_name">
                    {{ errors.brand_name }}
                  </div>
                </div>

                <!-- Descripción -->
                <div class="mb-4">
                  <label class="form-label">Descripción</label>
                  <textarea 
                    class="form-control"
                    v-model="form.description"
                    rows="4"
                    placeholder="Descripción del banco..."
                  ></textarea>
                </div>

                <!-- Botones -->
                <div class="d-flex gap-3 justify-content-end mt-4">
                  <router-link 
                    to="/admin/productos"
                    class="btn btn-outline-secondary"
                  >
                    Cancelar
                  </router-link>
                  <button 
                    type="submit" 
                    class="btn btn-primary"
                    :disabled="isSubmitting"
                  >
                    <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                    {{ isEditing ? 'Actualizar' : 'Crear' }} Banco
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { getBrand, createBrand, updateBrand } from '@/services/brands';
import { uploadImage } from '@/services/products';

const toast = useToast();
const route = useRoute();
const router = useRouter();

const defaultImage = 'https://res.cloudinary.com/dkpmp8fwq/image/upload/v1748839616/logo.c7d24e78_rdvx4o.png';
const isSubmitting = ref(false);
const isEditing = ref(false);
const errors = ref({});

const form = ref({
  brand_name: '',
  brand_id: '',
  description: '',
  image: ref(defaultImage)
});

const fetchBank = async (id) => {
  try {
    const data = await getBrand(id);
    form.value = data;
  } catch (error) {
    toast.error('Error al cargar la información del banco');
    router.push('/admin/bancos');
  }
};

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    errors.value = {};
    
    if (isEditing.value) {
      await updateBrand(route.params.id, form.value);
    } else {
      await createBrand(form.value);
    }
    
    toast.success(`Banco ${isEditing.value ? 'actualizado' : 'creado'} exitosamente`);
    router.push('/admin/bancos');
  } catch (error) {
    if (error.response?.data) {
      errors.value = error.response.data;
    }
    toast.error('Error al guardar el banco');
  } finally {
    isSubmitting.value = false;
  }
};

const uploadProgress = ref(0);

const handleImageUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validar tamaño del archivo (2MB máximo)
  if (file.size > 2 * 1024 * 1024) {
    toast.error('La imagen no debe superar los 2MB');
    event.target.value = ''; // Limpiar input
    return;
  }

  try {
    uploadProgress.value = 1; // Iniciar progreso
    const imageUrl = await uploadImage(file);
    form.value.image = imageUrl;
    uploadProgress.value = 100;
    toast.success('Imagen subida exitosamente');
  } catch (error) {
    toast.error('Error al subir la imagen: ' + error.message);
    uploadProgress.value = 0;
  }
};

onMounted(() => {
  if (route.params.id) {
    isEditing.value = true;
    fetchBank(route.params.id);
  }
});
</script>

<style scoped>
.card {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.form-control {
  background-color: var(--bs-dark);
  border: 1px solid var(--bs-gray-700);
  color: var(--bs-light);
}

.form-control:focus {
  background-color: var(--bs-dark);
  border-color: var(--bs-primary);
  color: var(--bs-light);
  box-shadow: 0 0 0 0.25rem rgba(var(--bs-primary-rgb), 0.25);
}

.required:after {
  content: " *";
  color: var(--bs-danger);
}

.form-control{
  background-color: var(--black-color-2);
  border: 1px solid var(--bs-gray-700);
  color: var(--bs-light);
}

.img-thumbnail {
  background-color: var(--bs-dark);
  border-color: var(--bs-gray-700);
}

.progress {
  height: 0.5rem;
  background-color: var(--bs-gray-700);
}

.progress-bar {
  background-color: var(--bs-primary);
  transition: width 0.2s ease;
}

.form-control[type="file"] {
  background-color: var(--black-color-2);
  color: var(--bs-light);
  border-color: var(--bs-gray-700);
  padding: 0.375rem;
}

.form-control[type="file"]::-webkit-file-upload-button {
  background-color: var(--bs-primary);
  color: white;
  border: none;
  padding: 0.375rem 0.75rem;
  margin-right: 1rem;
  border-radius: 0.25rem;
}
</style>
